// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { formatHumanName } from '@medplum/core';
import type { HumanName, Practitioner } from '@medplum/fhirtypes';
import { useResource } from '@medplum/react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { CareTeamNote } from '../../hooks/useCareTeamNotes';
import sectionClasses from './dashboard-section.module.css';

const ITEMS_PER_PAGE = 5;
const NOTE_PREVIEW_LENGTH = 80;

interface CareTeamNotesSectionProps {
  notes: CareTeamNote[];
  loading: boolean;
  onModalOpen: () => void;
}

/**
 * Patient-facing list of clinical notes, mirroring the design's "Notes from
 * Your Care Team" table. Clicking "Read Note" opens a modal with the full
 * note body and a "Send Message" CTA that lands the patient on the messages
 * inbox so they can follow up with the note's author.
 */
export function CareTeamNotesSection({ notes, loading, onModalOpen }: CareTeamNotesSectionProps): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [selectedNote, setSelectedNote] = useState<CareTeamNote | undefined>();
  const [modalOpened, modalHandlers] = useDisclosure(false);

  const totalPages = Math.max(1, Math.ceil(notes.length / ITEMS_PER_PAGE));
  const paginated = useMemo(() => notes.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE), [notes, page]);

  const openNote = (note: CareTeamNote): void => {
    setSelectedNote(note);
    modalHandlers.open();
    onModalOpen();
  };

  const handleSendMessage = (): void => {
    // Pre-selecting the thread for a specific Practitioner would require a
    // cross-resource Communication search (recipient + sender both match) which
    // the current ThreadInbox API doesn't expose; route to /messages and let
    // the patient pick the thread they want to follow up in.
    modalHandlers.close();
    navigate('/messages');
  };

  let body: JSX.Element;
  if (loading) {
    body = (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  } else if (notes.length === 0) {
    body = (
      <Text c="dimmed" py="md">
        You do not have any notes.
      </Text>
    );
  } else {
    body = (
      <>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Provider</Table.Th>
              <Table.Th>Note</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginated.map((note) => (
              <NoteRow key={note.id} note={note} onRead={openNote} />
            ))}
          </Table.Tbody>
        </Table>
        {totalPages > 1 && (
          <Group justify="space-between" mt="md">
            <Text size="sm" c="dimmed">
              {notes.length} {notes.length === 1 ? 'note' : 'notes'}
            </Text>
            <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
          </Group>
        )}
      </>
    );
  }

  return (
    <>
      <Paper shadow="sm" radius="md" p="lg" className={sectionClasses.decorated}>
        <Title order={3} mb="md">
          Notes from Your Care Team
        </Title>
        {body}
      </Paper>

      <Modal
        opened={modalOpened}
        onClose={modalHandlers.close}
        size="xl"
        centered
        withCloseButton={false}
        radius="lg"
        padding="xl"
      >
        {selectedNote && <NoteModalBody note={selectedNote} onClose={modalHandlers.close} onSend={handleSendMessage} />}
      </Modal>
    </>
  );
}

interface NoteRowProps {
  note: CareTeamNote;
  onRead: (note: CareTeamNote) => void;
}

function NoteRow({ note, onRead }: NoteRowProps): JSX.Element {
  const author = useResource(note.authorRef);
  return (
    <Table.Tr>
      <Table.Td>{note.dateISO ? dayjs(note.dateISO).format('MM/DD/YYYY') : '—'}</Table.Td>
      <Table.Td>
        <Anchor size="sm">{getAuthorDisplay(author)}</Anchor>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {note.text.length > NOTE_PREVIEW_LENGTH
            ? `${note.text.slice(0, NOTE_PREVIEW_LENGTH).trimEnd()}...`
            : note.text}
        </Text>
      </Table.Td>
      <Table.Td>
        <Anchor size="sm" component="button" type="button" onClick={() => onRead(note)}>
          Read Note
        </Anchor>
      </Table.Td>
    </Table.Tr>
  );
}

interface NoteModalBodyProps {
  note: CareTeamNote;
  onClose: () => void;
  onSend: () => void;
}

function NoteModalBody({ note, onClose, onSend }: NoteModalBodyProps): JSX.Element {
  const author = useResource(note.authorRef);
  const dateLine = formatNoteDateLine(note);
  return (
    <Stack gap="lg" align="center">
      <Title order={2} ta="center">
        Note from{' '}
        <Text component="span" inherit td="underline">
          {getAuthorDisplay(author)}
        </Text>
      </Title>
      {dateLine && (
        <Stack gap={2} align="center">
          <Text size="md" fw={700}>
            from your appointment on
          </Text>
          <Text size="xl" fw={700}>
            {dateLine}
          </Text>
        </Stack>
      )}
      <Box bg="gray.0" w="100%" p="lg" style={{ borderRadius: 16 }}>
        <Stack gap="lg" align="center">
          <Textarea
            value={note.text}
            readOnly
            autosize
            minRows={4}
            maxRows={12}
            w="100%"
            styles={{ input: { backgroundColor: 'var(--mantine-color-white)' } }}
          />
          <Group justify="center">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={onSend}>Send Message</Button>
          </Group>
        </Stack>
      </Box>
    </Stack>
  );
}

function getAuthorDisplay(author: ReturnType<typeof useResource>): string {
  if (author?.resourceType !== 'Practitioner') {
    return 'your care team';
  }
  const practitioner = author as Practitioner;
  const name = practitioner.name?.[0];
  return name ? formatHumanName(name as HumanName, { prefix: true }) : 'your care team';
}

function formatTimeOfDay(d: dayjs.Dayjs, withMeridiem: boolean): string {
  const fmt = d.minute() === 0 ? 'h' : 'h:mm';
  const meridiem = withMeridiem ? 'a' : '';
  return d.format(`${fmt}${meridiem}`).toUpperCase();
}

function formatNoteDateLine(note: CareTeamNote): string | undefined {
  const start = note.appointment?.start ?? note.dateISO;
  if (!start) {
    return undefined;
  }
  const startDate = dayjs(start);
  const end = note.appointment?.end;
  const datePart = startDate.format('MMMM D, YYYY');
  if (!end) {
    return `${datePart} at ${formatTimeOfDay(startDate, true)}`;
  }
  const endDate = dayjs(end);
  return `${datePart} at ${formatTimeOfDay(startDate, false)}–${formatTimeOfDay(endDate, true)}`;
}
